import type { Scale, Tick } from 'chart.js'

/**
 * #569 (08/10) — LE DERNIER REPÈRE DE L'AXE EST TOUJOURS AFFICHÉ.
 *
 * Chart.js saute des étiquettes quand la place manque (`ticks.autoSkip`, actif par défaut).
 * Dans une tuile étroite, la courbe DVF 2020-2025 s'étiquetait « 2020, 2022, 2024 » : le
 * point 2025 était tracé, mais rien ne le nommait, et la recette l'a lue « arrêtée en
 * 2024 ». Le saut reste (sans lui, les étiquettes se chevauchent) ; seul le DERNIER repère
 * est rétabli — à la place du dernier gardé quand il tomberait plus près qu'un pas, en plus
 * sinon, pour ne jamais chevaucher son voisin.
 */
export function garderDernierRepere<T extends { value: number }>(tous: T[], gardes: T[]): T[] {
  if (!tous.length || !gardes.length) return gardes
  const dernier = tous[tous.length - 1]
  const fin = gardes[gardes.length - 1]
  if (fin.value === dernier.value) return gardes
  const pas = gardes.length > 1 ? gardes[1].value - gardes[0].value : Infinity
  return dernier.value - fin.value < pas
    ? [...gardes.slice(0, -1), dernier]
    : [...gardes, dernier]
}

/**
 * Les deux crochets d'échelle qui encadrent le saut d'étiquettes dans `Scale.update()` :
 * `afterCalculateLabelRotation` voit encore TOUS les repères (étiquettes calculées),
 * `beforeFit` voit ceux que le saut a gardés, avant que les dimensions de l'axe ne soient
 * mesurées — y rétablir le dernier le fait mesurer et dessiner comme les autres.
 */
export function rappelsDernierRepere() {
  const complets = new WeakMap<object, Tick[]>()
  return {
    afterCalculateLabelRotation: (echelle: Scale) => {
      complets.set(echelle, echelle.ticks)
    },
    beforeFit: (echelle: Scale) => {
      const tous = complets.get(echelle)
      if (tous) echelle.ticks = garderDernierRepere(tous, echelle.ticks)
    },
  }
}
